import { Error } from '@redux/common/types';
import { ENDS_SELECT_OPTIONS, FREQUENCY_SELECT_OPTIONS } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { TriggerTypes } from 'components/TriggerSelectBlock/TriggerSelectBlock';

export enum TaskStatusEnum {
  ERROR = 'ERROR',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS',
  SCHEDULED = 'SCHEDULED',
  DELIVERED = 'DELIVERED',
  NO_DEVICE = 'NO_DEVICE',
  FOLLOW_UP = 'FOLLOW UP'
}

export interface TasksListItemType {
  taskId: string;
  patientFullName: string;
  taskMessage?: string;
  taskName?: string;
  taskSummary?: string;
  patientResponse?: string;
  scheduledForDateTime: string;
  status: TaskStatusEnum;
  frequency: string;
  taskType: string;
}

export interface RecurringTaskType {
  id: string;
  scheduledForDateTime: string;
  taskName: string;
  status: TaskStatusEnum;
}

export interface RecurringTasksListView {
  taskId: string;
  taskName: string;
  taskSummary?: string;
  scheduledForDateTime: string;
  taskStatus: string;
}

export interface TaskSummaryType {
  taskId: string;
  taskName: string;
  taskSummary?: string;
  taskType: string;
  engagementPlanName?: string;
  numberOfTasksCompleted?: number;
  numberOfTasksTotal?: number;
  scheduledForDateTime: string;
  taskStatus: string;
  recurringTasksList?: RecurringTasksListView[];
}

export interface TaskDetailsType {
  id: string;
  scheduledForDateTime: string;
  status: TaskStatusEnum;
  assignee: TaskAssigneeType;
  assigneeFullName: string;
  logs: TaskLogsType[];
  frequency: FREQUENCY_SELECT_OPTIONS;
  frequencyEnds?: ENDS_SELECT_OPTIONS;
  frequencyEndsOn?: string;
  deliverableMessage?: string;
  rawMessage?: string;
  expirationDateTime: string;
  endsAfterOccurrences?: string;
  triggerCondition?: TriggerTypes;
  taskName: string;
  taskType: TaskTypesEnum;
  assessmentId?: string;
  assessmentName?: string;
  assessmentDescription?: string;
  assessmentResult?: string;
}

export interface TaskAssigneeType {
  assigneeId: string;
  assigneeFullname: string;
  assigneeEmail: string;
}

export interface TaskLogsType {
  id: string;
  logTitle: string;
  logCreated: string;
  logMessage?: string;
}

export interface TaskMessageGenerationPayloadType {
  taskMessage: string;
  assigneeId: string;
  assigneeFullName: string;
  scheduledForDateTime: string;
}

export interface TaskMessageGenerationResponseType {
  generatedMessage: string;
  taskName: string;
  taskSummary?: string;
}

export interface TaskCreationPayloadType extends TaskMessageGenerationPayloadType {
  generatedMessage: string;
  taskName: string;
  taskType: string;
  assessmentId?: string;
  frequency: FREQUENCY_SELECT_OPTIONS;
  frequencyEnds: Nullable<ENDS_SELECT_OPTIONS>;
  endsOnDate: Nullable<string>;
  endsAfterOccurrences: Nullable<number>;
  triggerCondition: TriggerTypes;
  expiredForDateTime: string;
  responseRequired?: boolean;
  taskSummary?: string;
}

export enum TaskTypesEnum {
  MESSAGE = 'MESSAGE',
  ASSESSMENT = 'ASSESSMENT'
}

export interface EngagementPlan {
  id: string;
  title: string;
  description?: string;
  durationDays: number;
  taskCount: number;
  imageSource?: string;
  tasks?: EngagementPlanTask[];
  organizationIds?: string[] | null;
  canDuplicate?: boolean;
}

export interface PatientEngagementPlan {
  id: string; // patientEngagementPlanId
  engagementPlanId: string;
  title: string;
  description?: string;
  durationDays: number;
  taskCount: number;
  imageSource?: string;
  startDate?: string;
  status?: string;
  adherencePercent?: number;
  completedTaskCount?: number;
  organizationIds?: string[] | null;
}

export interface EngagementPlanTask {
  taskType: string;
  taskName: string;
  frequencyType: string;
  endsAfterOccurrences: number;
  deliveryStartTime: string;
  deliveryExpirationTime: string;
  deliverableMessage: string;
  expectedResponse: boolean;
  responseType: string;
  triggerCondition: string;
}

export interface TasksState {
  readonly isLoading: Record<string, boolean>;
  readonly error: Record<string, Error>;
  readonly success: Record<string, boolean>;
  tasksList: TasksListItemType[];
  selectedTaskDetails: Nullable<TaskDetailsType>;
  generatedTaskMessage: Nullable<string>;
  generatedTaskName: Nullable<string>;
  generatedTaskSummary: Nullable<string>;
  personUpcomingTasks: TasksListItemType[];
  personTasksByDateRangeTasks: TaskSummaryType[];
  engagementPlans: EngagementPlan[];
  patientEngagementPlans: PatientEngagementPlan[];
  updatingEngagementPlan: Nullable<EngagementPlan>;
  assessmentsList: Nullable<AssessmentListItemType[]>;
  selectedAssessment: Nullable<AssessmentListItemType>;
  previewAssessmentId: Nullable<string>;
  previewAssessmentDetails: Nullable<PreviewAssessmentDetailsType | AssessmentResultType>;
}

export interface AssessmentListItemType {
  id: string;
  name: string;
  description: string;
}

export interface AssessmentMessageGenerationPayloadType {
  assessmentName: string;
  personFullName: string;
  scheduledDateTime: string;
}

export interface AssessmentMessageGenerationResponseType {
  startingMessage: string;
}

export interface PreviewAssessmentDetailsType {
  id: string;
  name: string;
  questions: PreviewAssessmentQuestionDetailsType[];
}

export interface PreviewAssessmentQuestionDetailsType {
  question: string;
  options: string[];
}

export interface AssessmentResultType {
  id: string;
  name: string;
  result: string;
  questions: AssessmentResultQuestionDetailsType[];
}

export interface AssessmentResultQuestionDetailsType {
  question: string;
  answer: string;
}
